import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Check, ArrowRight, Menu, X, AlertCircle, BadgeCheck, Clock, GraduationCap, BookOpen, Heart, Lightbulb } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Concession definitions — single source of truth for the unified Community sign-up.
// All four concession types resolve to the same `community` Stripe tier (£1.00/mo).
const CONCESSIONS = {
  nhs: {
    label: 'NHS Staff',
    icon: Heart,
    idLabel: 'NHS Smartcard ID',
    placeholder: 'e.g. 123456789012',
    helper: '12 digits',
    pattern: /^\d{12}$/,
    error: 'NHS Smartcard ID must be exactly 12 digits.',
    maxLength: 12,
  },
  blue_light: {
    label: 'Blue Light Card',
    icon: Shield,
    idLabel: 'Blue Light Card Number',
    placeholder: 'e.g. ABC1234567',
    helper: '3 letters + 7 digits',
    pattern: /^[A-Za-z]{3}\d{7}$/,
    error: 'Blue Light Card must be 3 letters followed by 7 digits.',
    maxLength: 10,
  },
  student: {
    label: 'Student',
    icon: GraduationCap,
    idLabel: 'NUS / TOTUM Card Number',
    placeholder: 'e.g. 6044890000123456',
    helper: '16 digits — found on the back of your NUS / TOTUM card',
    pattern: /^\d{16}$/,
    error: 'NUS / TOTUM card number must be exactly 16 digits.',
    maxLength: 16,
  },
  teacher: {
    label: 'Teacher',
    icon: BookOpen,
    idLabel: 'Teacher Reference Number (TRN)',
    placeholder: 'e.g. 1234567',
    helper: '7 digits — your DfE Teacher Reference Number',
    pattern: /^\d{7}$/,
    error: 'Teacher Reference Number must be exactly 7 digits.',
    maxLength: 7,
  },
};

export default function CommunityLandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('register');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [concessionId, setConcessionId] = useState('');
  const [concessionIdError, setConcessionIdError] = useState('');

  // Pre-select concession type from ?type= or legacy ?ref= query param.
  // Examples: /community?type=student, /community?type=teacher, /community?type=blue_light
  // Legacy URLs like /nhs?ref=students still work.
  const urlParams = new URLSearchParams(window.location.search);
  const referrerCode = urlParams.get('ref') || '';
  const typeCode = urlParams.get('type') || '';
  const initialConcession = useMemo(() => {
    const r = (typeCode || referrerCode).toLowerCase();
    if (r === 'students' || r === 'student') return 'student';
    if (r === 'teachers' || r === 'teacher') return 'teacher';
    if (r === 'blue_light' || r === 'bluelight' || r === 'blue-light') return 'blue_light';
    return 'nhs';
  }, [typeCode, referrerCode]);
  const [concessionType, setConcessionType] = useState(initialConcession);

  const cfg = CONCESSIONS[concessionType];

  const validateConcessionId = (value, type = concessionType) => {
    const t = (value || '').trim();
    const c = CONCESSIONS[type];
    if (!t) return `Please enter your ${c.idLabel}`;
    if (c.pattern.test(t)) return '';
    return c.error;
  };

  const handleConcessionIdChange = (e) => {
    setConcessionId(e.target.value);
    if (concessionIdError && e.target.value.length >= 6) {
      setConcessionIdError(validateConcessionId(e.target.value));
    }
  };

  const switchConcession = (type) => {
    setConcessionType(type);
    setConcessionId('');
    setConcessionIdError('');
  };

  const handleSubmit = async () => {
    const idErr = validateConcessionId(concessionId);
    if (idErr) { setConcessionIdError(idErr); return; }
    setLoading(true);
    setError('');
    try {
      if (formMode === 'register') {
        const res = await fetch(`${API}/api/auth/register/nhs`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({
            name, email, password,
            concession_type: concessionType,
            concession_id: concessionId.trim().toUpperCase(),
            referral_source: `${concessionType}_landing`,
            referral_code: referrerCode || undefined,
          })
        });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.detail || data.error || 'Registration failed. This email may already be registered.'); setLoading(false); return; }
      } else {
        const form = new FormData();
        form.append('email', email);
        form.append('password', password);
        const res = await fetch(`${API}/api/auth/login`, { method: 'POST', credentials: 'include', body: form });
        const data = await res.json();
        if (!res.ok || data.error) { setError(data.detail || data.error || 'Login failed.'); setLoading(false); return; }
        // Best-effort verify update for existing users
        await fetch(`${API}/api/auth/nhs-verify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ concession_type: concessionType, concession_id: concessionId.trim().toUpperCase() })
        }).catch(() => {});
      }
      const checkoutRes = await fetch(`${API}/api/stripe-payments/checkout/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ tier_id: 'community', billing_cycle: billingCycle, origin_url: window.location.origin })
      });
      const checkoutData = await checkoutRes.json();
      if (checkoutData.success && checkoutData.checkout_url) window.location.href = checkoutData.checkout_url;
      else setError(checkoutData.detail || 'Failed to create checkout. Please try again.');
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };

  const NAV = [
    { label: "How it works", href: "/#how-it-works" },
    { label: "Fuel", href: "/#fuel" },
    { label: "Community", href: "/community" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ];

  return (
    <div style={{ fontFamily: "Manrope,sans-serif" }} className="bg-[#FAFAFA] text-zinc-900 min-h-screen flex flex-col">
      <Helmet>
        <title>Community Access — NHS, Blue Light, Students & Teachers · PricePAL Compare</title>
        <meta name="description" content="NHS staff, Blue Light Card holders, students and teachers all get the same £1.00/month Community rate. Verify your status and unlock the full app." />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center"><span className="text-white font-black text-xs" style={{ fontFamily: "Outfit,sans-serif" }}>P</span></div>
            <span className="text-base font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}><span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span></span>
          </a>
          <div className="flex items-center gap-3">
            <a href="/register" className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold px-4 py-2 rounded-full text-sm transition flex items-center gap-1.5 shadow-sm">Get app <ArrowRight className="w-3.5 h-3.5" /></a>
            <button className="md:hidden p-1.5 text-zinc-600" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          <nav className="hidden md:flex items-center gap-7 ml-8">
            {NAV.map(l => (
              <a key={l.href} href={l.href} className={`text-sm font-medium transition ${l.href === '/community' ? 'text-blue-600 font-bold' : 'text-zinc-500 hover:text-emerald-600'}`}>{l.label}</a>
            ))}
          </nav>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-100 px-4 py-4 space-y-3">
            {NAV.map(l => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className={`block text-sm font-medium py-2 ${l.href === '/community' ? 'text-blue-600 font-bold' : 'text-zinc-600'}`}>{l.label}</a>
            ))}
          </div>
        )}
      </header>
      <div className="h-14" />

      <main className="flex-1">
        {/* Hero */}
        <section className="px-4 sm:px-6 py-10 md:py-16 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 border border-blue-100" data-testid="nhs-badge">
              <Shield className="w-3.5 h-3.5" /> NHS · Blue Light · Students · Teachers
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-zinc-900 leading-tight mb-4" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="nhs-heading">
              Community <span className="text-blue-600">access</span>
            </h1>
            <p className="text-sm sm:text-base text-zinc-500 leading-relaxed mb-2">
              For NHS staff, Blue Light Card holders, students (NUS / TOTUM), and teachers — verify your status and unlock the full app for just <span className="font-bold text-blue-700">£1.00/month</span>.
            </p>
            <p className="text-sm text-zinc-500">One discount price for everyone who serves their community.</p>
          </div>
        </section>

        {/* What's included */}
        <section className="px-4 sm:px-6 pb-10 md:pb-14">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-4 text-center" style={{ fontFamily: "Outfit,sans-serif" }}>What's included</h2>
            <div className="bg-white rounded-2xl border border-zinc-100 p-5 space-y-3">
              {[
                'Unlimited barcode scanning',
                'Compare prices across 10 UK retailers',
                'Price drop alerts',
                'AI receipt scanning',
                'Kitchen AI meal planning',
                'Fuel price finder',
                'Family sharing (up to 4 members)',
                'Priority customer support',
                'Early feature releases',
              ].map(f => (
                <div key={f} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-blue-600" /></div>
                  <span className="text-sm text-zinc-700">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing + Verification */}
        <section className="px-4 sm:px-6 pb-14 md:pb-20">
          <div className="max-w-lg mx-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-1 text-center" style={{ fontFamily: "Outfit,sans-serif" }}>Verify your access</h2>
            <p className="text-sm text-zinc-500 text-center mb-6">Pick the concession that applies to you and verify with your ID.</p>

            <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden" data-testid="nhs-pricing-card">
              {/* Billing toggle */}
              <div className="flex justify-center p-5 pb-3">
                <div className="bg-zinc-100 rounded-lg p-1 flex gap-1">
                  <button data-testid="nhs-monthly-toggle" className={`px-4 py-2 rounded-md text-sm font-medium transition ${billingCycle === 'monthly' ? 'bg-white shadow text-blue-700' : 'text-zinc-500'}`} onClick={() => setBillingCycle('monthly')}>Monthly</button>
                  <button data-testid="nhs-annual-toggle" className={`px-4 py-2 rounded-md text-sm font-medium transition ${billingCycle === 'annual' ? 'bg-white shadow text-blue-700' : 'text-zinc-500'}`} onClick={() => setBillingCycle('annual')}>Annual <span className="text-emerald-600 text-xs ml-1">Save 17%</span></button>
                </div>
              </div>

              {/* Price */}
              <div className="text-center px-5 py-3">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-black text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="nhs-price">£{billingCycle === 'monthly' ? '1.00' : '0.83'}</span>
                  <span className="text-zinc-400 text-sm">/month</span>
                </div>
                {billingCycle === 'annual' && <p className="text-xs text-zinc-400 mt-1">Billed as £10.00/year</p>}
                <p className="text-xs text-emerald-600 font-medium mt-2 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> 7 days free · Card required · Cancel anytime</p>
              </div>

              {/* CTA / Form */}
              <div className="px-5 pb-5">
                {!showForm ? (
                  <button data-testid="nhs-get-started-btn" onClick={() => setShowForm(true)} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-full transition text-base font-bold min-h-[48px]">
                    <Shield className="w-4 h-4" /> Get Community access <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="space-y-3" data-testid="nhs-signup-form">
                    {/* Mode toggle */}
                    <div className="flex gap-1 bg-zinc-100 rounded-lg p-1">
                      <button className={`flex-1 py-2 rounded-md text-sm font-medium transition ${formMode === 'register' ? 'bg-white shadow text-blue-700' : 'text-zinc-500'}`} onClick={() => setFormMode('register')} data-testid="mode-register">New Account</button>
                      <button className={`flex-1 py-2 rounded-md text-sm font-medium transition ${formMode === 'login' ? 'bg-white shadow text-blue-700' : 'text-zinc-500'}`} onClick={() => setFormMode('login')} data-testid="mode-login">Existing Account</button>
                    </div>

                    {/* Concession-type selector — pick which concession applies to you */}
                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-2">Which applies to you?</label>
                      <div className="grid grid-cols-2 gap-2" data-testid="concession-selector">
                        {Object.entries(CONCESSIONS).map(([key, c]) => {
                          const Icon = c.icon;
                          const active = concessionType === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => switchConcession(key)}
                              data-testid={`concession-${key}`}
                              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition ${active ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-zinc-200 text-zinc-600 hover:border-blue-300'}`}
                            >
                              <Icon className="w-4 h-4" />
                              {c.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Concession ID input — format adapts to selected concession */}
                    <div>
                      <label className="block text-sm font-semibold text-zinc-700 mb-1">
                        <BadgeCheck className="w-3.5 h-3.5 inline mr-1 text-blue-600" />
                        {cfg.idLabel}
                      </label>
                      <input
                        data-testid="concession-id-input"
                        type="text"
                        value={concessionId}
                        onChange={handleConcessionIdChange}
                        placeholder={cfg.placeholder}
                        maxLength={cfg.maxLength}
                        className={`w-full px-4 py-3 border rounded-xl text-sm ${concessionIdError ? 'border-red-400 bg-red-50' : 'border-zinc-200'} focus:outline-none focus:border-blue-500 bg-white`}
                      />
                      {concessionIdError && <p className="text-red-500 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{concessionIdError}</p>}
                      <p className="text-[11px] text-zinc-400 mt-1">{cfg.helper}</p>
                    </div>

                    {formMode === 'register' && (
                      <div>
                        <label className="block text-sm font-medium text-zinc-700 mb-1">Full Name</label>
                        <input data-testid="nhs-name-input" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name"
                          className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 bg-white" />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">Email Address</label>
                      <input data-testid="nhs-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.name@example.com"
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 bg-white" />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-zinc-700 mb-1">{formMode === 'register' ? 'Create Password' : 'Password'}</label>
                      <input data-testid="nhs-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={formMode === 'register' ? 'At least 6 characters' : 'Your password'}
                        className="w-full px-4 py-3 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:border-blue-500 bg-white" />
                    </div>

                    <button data-testid="nhs-subscribe-btn" onClick={handleSubmit} disabled={loading || !email || !password || !concessionId || (formMode === 'register' && !name)}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-full transition text-base font-bold disabled:opacity-50 min-h-[48px]">
                      {loading ? 'Setting up...' : <><Shield className="w-4 h-4" />{formMode === 'register' ? 'Create Account & Start 7-Day Trial' : 'Log In & Start 7-Day Trial'}<ArrowRight className="w-4 h-4" /></>}
                    </button>

                    {error && <p className="text-red-500 text-sm text-center flex items-center justify-center gap-1" data-testid="signup-error"><AlertCircle className="w-3.5 h-3.5" />{error}</p>}
                  </div>
                )}
                <p className="text-[11px] text-zinc-400 text-center mt-3">Secure checkout powered by Stripe. Cancel anytime during your trial.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-4 sm:px-6 pb-14 md:pb-20">
          <div className="max-w-lg mx-auto">
            <h2 className="text-lg font-bold text-zinc-900 mb-4 text-center" style={{ fontFamily: "Outfit,sans-serif" }}>Frequently asked questions</h2>
            <div className="space-y-3">
              {[
                { q: 'How do I verify my eligibility?', a: "Enter your NHS ID (12 digits) or Blue Light Card ID (3 letters + 7 digits) when signing up." },
                { q: 'What happens after the 7-day trial?', a: `Your card will be charged £${billingCycle === 'monthly' ? '1.00/month' : '10.00/year'}. Cancel anytime before the trial ends and you won't be charged.` },
                { q: 'Can I cancel anytime?', a: 'Yes. Go to Settings > Billing & Subscription > Manage Subscription. Access continues until end of billing period.' },
                { q: 'Is this price permanent?', a: 'Yes. Your NHS & Blue Light Card rate is locked in for as long as you stay subscribed.' },
                { q: 'Can my family use it?', a: 'Your plan includes family sharing for up to 4 members.' },
              ].map((faq, i) => (
                <details key={i} className="bg-white border border-zinc-100 rounded-xl p-4">
                  <summary className="font-medium text-sm text-zinc-800 cursor-pointer">{faq.q}</summary>
                  <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-50 py-8 pb-20 sm:pb-8 border-t border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-zinc-400">&copy; 2026 PricePAL Compare Ltd.</span>
          <nav className="flex gap-5">
            {[{ label: "About", href: "/about" }, { label: "Contact", href: "/contact" }, { label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" }].map(l => (
              <a key={l.href} href={l.href} className="text-sm text-zinc-400 hover:text-emerald-600 transition py-2 min-h-[44px] flex items-center">{l.label}</a>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
